import { useState } from 'preact/hooks';
import type { Currency, NonTradableItem } from '../../shared/types';
import { useMask } from '../lib/mask';
import { EditNonTradablePopover } from './EditNonTradablePopover';
import './../styles/components/panels.css';
import './../styles/components/edit-popover.css';

const LIQUIDITY_LABEL: Record<NonTradableItem['liquidity'], string> = {
  low: 'Low',
  very_low: 'Very low',
  illiquid: 'Illiquid',
};

export function NonTradableSection({
  items,
  ccy,
  onSaved,
}: {
  items: NonTradableItem[];
  ccy: Currency;
  onSaved?: () => void;
}) {
  const { amount } = useMask();
  const [editId, setEditId] = useState<string | null>(null);

  const sorted = [...items].sort((a, b) => {
    const va = ccy === 'USD' ? (a.value_usd ?? 0) : (a.value_eur ?? 0);
    const vb = ccy === 'USD' ? (b.value_usd ?? 0) : (b.value_eur ?? 0);
    return vb - va;
  });
  const total = sorted.reduce((s, it) => {
    const v = ccy === 'USD' ? it.value_usd : it.value_eur;
    return s + (v ?? 0);
  }, 0);
  const tbdCount = items.filter(
    (it) => (ccy === 'USD' ? it.value_usd : it.value_eur) == null
  ).length;

  return (
    <section class="panel">
      <h3 class="panel-title">Private investments (lower liquidity)</h3>
      <ul class="nontr">
        <li class="nontr-row nontr-row-head mono">
          <span class="nontr-colhead">Asset</span>
          <span class="nontr-colhead">Liquidity</span>
          <span class="nontr-colhead right">Est. Value (EUR)</span>
        </li>
        {sorted.map((it) => {
          const v = ccy === 'USD' ? it.value_usd : it.value_eur;
          return (
            <li class="nontr-row" key={it.id}>
              <span class="nontr-name">{it.name}</span>
              <span class={`liq-pill liq-${it.liquidity}`}>{LIQUIDITY_LABEL[it.liquidity]}</span>
              <span class="nontr-value mono nontr-value-host">
                <button
                  type="button"
                  class="htable-edit-trigger"
                  onClick={() => setEditId(it.id)}
                  aria-label={`Edit ${it.name} value`}
                >
                  {v != null ? amount(v, ccy) : <em class="dim">TBD</em>}
                </button>
                {editId === it.id && (
                  <EditNonTradablePopover
                    id={it.id}
                    name={it.name}
                    valueEur={it.value_eur}
                    onClose={() => setEditId(null)}
                    onSaved={() => {
                      setEditId(null);
                      onSaved?.();
                    }}
                  />
                )}
              </span>
            </li>
          );
        })}
        <li class="nontr-row nontr-row-total">
          <span class="nontr-name">Total</span>
          <span class="dim mono nontr-total-note">
            {tbdCount > 0 ? `${tbdCount} pending valuation` : ''}
          </span>
          <span class="nontr-value mono">{amount(total, ccy)}</span>
        </li>
      </ul>
    </section>
  );
}
